import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from './api';
import IconRelation from '../../components/icons/IconRelation';
import IconFileText from '../../components/icons/IconFileText';
import '../../css/modals.css';
import '../../css/course-dependencies.css';

// Kind labels and order mirror CourseDependencyService on the backend. The
// backend sends every row with its required children (requires) and any
// blockers that come from outside the course (blockers).
const KIND_LABELS = {
   COURSE: 'Курс',
   MODULE: 'Модуль',
   CHAPTER: 'Розділ',
   UNIT: 'Юніт',
   LESSON: 'Урок курсу',
   TOPIC: 'Тема',
   CONTAINER: 'Контейнер',
   ELEMENT: 'Блок',
   EXLINK: 'Вправа в темі',
   LESSONLINK: 'Прив\'язка до уроку',
   EXERCISE: 'Вправа-шаблон',
   ASSIGN: 'Призначення',
   WORKSPACE: 'Простір студента',
   WS_ANSWERS: 'Відповіді',
   WS_CONV: 'Розмови',
   WS_NOTES: 'Нотатки',
   WS_MARKERS: 'Маркери',
   WS_CANVAS: 'Канвас',
   WS_LOGS: 'Журнал',
};

const KIND_ORDER = ['COURSE', 'MODULE', 'CHAPTER', 'UNIT', 'LESSON', 'TOPIC', 'CONTAINER', 'ELEMENT', 'EXLINK',
   'LESSONLINK', 'EXERCISE', 'ASSIGN', 'WORKSPACE', 'WS_ANSWERS', 'WS_CONV', 'WS_NOTES', 'WS_MARKERS', 'WS_CANVAS', 'WS_LOGS'];

const GROUPS = [
   { key: 'course', title: 'Структура курсу' },
   { key: 'topics', title: 'Теми, блоки та прив\'язки' },
   { key: 'exercises', title: 'Вправи-шаблони' },
   { key: 'students', title: 'Студенти та їх прогрес' },
];

// What deleting a row means. link = only the connection to the course goes
// (the thing on the other end stays); element = the row itself is removed for
// good. text is the hover tooltip shown on the row's icon.
const ROLE = {
   ASSIGN: { link: true, text: 'Зв\'язок: знімає лише призначення студента до курсу. Сам студент лишається.' },
   EXLINK: { link: true, text: 'Зв\'язок: знімає вправу з теми. Вправа-шаблон лишається в бібліотеці.' },
   LESSONLINK: { link: true, text: 'Зв\'язок: знімає тему з уроку. Урок у календарі лишається.' },
   TOPIC: { link: false, text: 'Кінцевий елемент: тема з усім вмістом, видаляється назавжди. Тема, яку не вибрано, лише відв\'язується від курсу і лишається в бібліотеці.' },
   EXERCISE: { link: false, text: 'Кінцевий елемент: вправа-шаблон, видаляється назавжди. Лише якщо її не використовують поза цим курсом.' },
   WORKSPACE: { link: false, text: 'Кінцевий елемент: прогрес студента в курсі, видаляється назавжди. Без вибору цього рядка курс не видалиться.' },
   WS_ANSWERS: { link: false, text: 'Кінцевий елемент: збережені відповіді студента, видаляються назавжди.' },
   WS_CONV: { link: false, text: 'Кінцевий елемент: прогрес розмов студента, видаляється назавжди.' },
   WS_NOTES: { link: false, text: 'Кінцевий елемент: нотатки студента, видаляються назавжди.' },
   WS_MARKERS: { link: false, text: 'Кінцевий елемент: маркери студента, видаляються назавжди.' },
   WS_CANVAS: { link: false, text: 'Кінцевий елемент: елементи канвасу студента, видаляються назавжди.' },
   WS_LOGS: { link: false, text: 'Кінцевий елемент: журнал активності студента, видаляється назавжди.' },
   CONTAINER: { link: false, text: 'Кінцевий елемент: блок вмісту теми, видаляється назавжди.' },
   ELEMENT: { link: false, text: 'Кінцевий елемент: блок вмісту теми, видаляється назавжди.' },
   MODULE: { link: false, text: 'Кінцевий елемент: частина структури курсу, видаляється назавжди.' },
   CHAPTER: { link: false, text: 'Кінцевий елемент: частина структури курсу, видаляється назавжди.' },
   UNIT: { link: false, text: 'Кінцевий елемент: частина структури курсу, видаляється назавжди.' },
   LESSON: { link: false, text: 'Кінцевий елемент: урок курсу, видаляється назавжди.' },
   COURSE: { link: false, text: 'Кінцевий елемент: сам курс.' },
};
const roleOf = (kind) => ROLE[kind] || { link: false, text: 'Кінцевий елемент.' };

const OWNED_LABELS = {
   markers: 'маркерів',
   canvas: 'канвас-елементів',
   notes: 'нотаток',
   approvals: 'запитів на перевірку',
   elements: 'блоків',
   interactive: 'інтерактивних блоків',
   words: 'слів',
   rows: 'записів',
};

const MAX_SKIPPED_SHOWN = 30;
const NO_ITEMS = [];

const kindOf = (key) => key.split(':')[0];

// Adds the given keys plus everything they require, recursively. Checking a
// row checks its whole required subtree, so the user doesn't tick every block.
function withRequirements(keys, byKey) {
   const out = new Set();
   const stack = [...keys];
   while (stack.length) {
      const key = stack.pop();
      if (out.has(key)) continue;
      out.add(key);
      const item = byKey[key];
      if (item) item.requires.forEach((required) => stack.push(required));
   }
   return out;
}

// Required rows that aren't selected, grouped by kind. Only meaningful while the
// item itself is selected - an unselected parent isn't blocked, just not chosen.
function missingRequires(item, selected) {
   const counts = {};
   item.requires.forEach((required) => {
      if (!selected.has(required)) {
         const kind = kindOf(required);
         counts[kind] = (counts[kind] || 0) + 1;
      }
   });
   return Object.keys(counts).map((kind) => (KIND_LABELS[kind] || kind) + ' (' + counts[kind] + ')');
}

export default function CourseDeleteModal({ isOpen, onClose, courseId, onChanged, onCourseDeleted }) {
   const navigate = useNavigate();
   const [inventory, setInventory] = useState(null);
   const [loading, setLoading] = useState(false);
   const [loadError, setLoadError] = useState('');
   const [selected, setSelected] = useState(() => new Set());
   const [report, setReport] = useState(null);
   const [busy, setBusy] = useState(false);
   // Set after a partial delete so closing the modal reloads the constructor
   // (its tree is loaded once by useCourseWorkspace and would otherwise be stale).
   const changedRef = useRef(false);

   const loadInventory = () => {
      setLoading(true);
      setLoadError('');
      return api.getCourseDependencies(courseId)
         .then((response) => {
            setInventory(response.data);
            setSelected(new Set());
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) navigate('/login');
            setLoadError('Не вдалося завантажити залежності курсу.');
         })
         .finally(() => setLoading(false));
   };

   useEffect(() => {
      if (!isOpen) return;
      setReport(null);
      loadInventory();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, courseId]);

   const items = inventory ? inventory.items : NO_ITEMS;
   const byKey = useMemo(() => {
      const map = {};
      items.forEach((item) => { map[item.key] = item; });
      return map;
   }, [items]);

   if (!isOpen) return '';

   const groups = GROUPS.map((group) => ({
      ...group,
      items: items.filter((item) => item.group === group.key)
         .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)),
   })).filter((group) => group.items.length > 0);

   const selectedCount = selected.size;
   const deletesCourse = selected.has('COURSE:' + courseId);
   const blockedCount = items.filter((item) => selected.has(item.key)
      && (item.blockers.length > 0 || missingRequires(item, selected).length > 0)).length;

   const stopProp = (e) => e.stopPropagation();

   const close = () => {
      if (changedRef.current) {
         changedRef.current = false;
         if (onChanged) onChanged();
      }
      onClose();
   };

   const toggleItem = (item) => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(item.key)) {
            next.delete(item.key);
         } else {
            withRequirements([item.key], byKey).forEach((key) => next.add(key));
         }
         return next;
      });
   };

   const toggleGroup = (group) => {
      const keys = group.items.map((item) => item.key);
      const allOn = keys.every((key) => selected.has(key));
      setSelected((prev) => {
         const next = new Set(prev);
         if (allOn) {
            keys.forEach((key) => next.delete(key));
         } else {
            withRequirements(keys, byKey).forEach((key) => next.add(key));
         }
         return next;
      });
   };

   const selectAll = () => setSelected(new Set(items.map((item) => item.key)));
   const clearAll = () => setSelected(new Set());

   const run = (dryRun) => {
      if (busy || selectedCount === 0) return;
      if (!dryRun) {
         const what = deletesCourse
            ? 'Курс буде видалено разом із вибраним. '
            : '';
         if (!window.confirm(what + 'Видалити вибрані позиції (' + selectedCount + ')? Цю дію не можна скасувати.')) return;
      }
      setBusy(true);
      api.deleteCourseDependencies(courseId, [...selected], dryRun)
         .then((response) => {
            const result = response.data;
            setReport(result);
            if (dryRun) return;
            changedRef.current = true;
            if (result.deleted.indexOf('COURSE:' + courseId) !== -1) {
               changedRef.current = false;
               onClose();
               // From the list the list just refreshes; from a course page there is
               // nothing left to show, so go back to the list.
               if (onCourseDeleted) onCourseDeleted();
               else navigate('/courses');
               return;
            }
            return loadInventory();
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) {
               navigate('/login');
               return;
            }
            const text = err.response && typeof err.response.data === 'string'
               ? err.response.data
               : 'Не вдалося виконати запит.';
            setReport({ error: text, deleted: [], skipped: [], counts: {}, dryRun: dryRun });
         })
         .finally(() => setBusy(false));
   };

   const renderItem = (item) => {
      const checked = selected.has(item.key);
      const missing = checked ? missingRequires(item, selected) : [];
      const external = item.blockers;
      const owned = Object.keys(item.owned);
      const blocked = external.length > 0 || missing.length > 0;
      return (
         <label key={item.key} className={'course-deps-item' + (checked ? ' is-selected' : '') + (blocked ? ' is-blocked' : '')}>
            <input type='checkbox' checked={checked} onChange={() => toggleItem(item)} />
            <div className='course-deps-item-main'>
               <div className='course-deps-item-title'>
                  <span className={'course-deps-role ' + (roleOf(item.kind).link ? 'is-link' : 'is-element')}
                     title={roleOf(item.kind).text}>
                     {roleOf(item.kind).link ? <IconRelation /> : <IconFileText />}
                  </span>
                  <span className='course-deps-kind'>{KIND_LABELS[item.kind] || item.kind}</span>
                  <span>{item.label}</span>
                  {item.detail ? <span className='course-deps-muted'>{item.detail}</span> : ''}
               </div>
               {owned.length || external.length || missing.length ? (
                  <div className='course-deps-chips'>
                     {owned.map((code) => (
                        <span key={code} className='course-deps-chip'>{(OWNED_LABELS[code] || code) + ': ' + item.owned[code]}</span>
                     ))}
                     {external.map((text, i) => (
                        <span key={'x' + i} className='course-deps-chip is-blocker'>Заблоковано: {text}</span>
                     ))}
                     {missing.map((text, i) => (
                        <span key={'m' + i} className='course-deps-chip is-blocker'>Потрібно також вибрати: {text}</span>
                     ))}
                  </div>
               ) : ''}
            </div>
         </label>
      );
   };

   const renderReport = () => {
      if (report.error) {
         return <div className='course-deps-report is-error'>{report.error}</div>;
      }
      const tables = Object.keys(report.counts || {}).filter((t) => report.counts[t] > 0);
      return (
         <div className='course-deps-report'>
            <div className='course-deps-report-title'>{report.dryRun ? 'Перевірка: нічого не видалено' : 'Готово'}</div>
            <div>
               {report.dryRun ? 'Буде видалено' : 'Видалено'} позицій: {report.deleted.length}
               {report.skipped.length ? ', пропущено: ' + report.skipped.length : ''}
            </div>
            {!report.dryRun && tables.length ? (
               <div className='course-deps-muted'>Рядків у таблицях: {tables.map((t) => t + ' ' + report.counts[t]).join(', ')}</div>
            ) : ''}
            {report.skipped.slice(0, MAX_SKIPPED_SHOWN).map((s) => (
               <div key={s.key} className='course-deps-skipped'><b>{s.label}</b>: {s.reasons.join('; ')}</div>
            ))}
            {report.skipped.length > MAX_SKIPPED_SHOWN ? (
               <div className='course-deps-muted'>...ще {report.skipped.length - MAX_SKIPPED_SHOWN}</div>
            ) : ''}
         </div>
      );
   };

   return (
      <div className='modal-update' onClick={close} style={{ display: 'flex' }}>
         <div className='modal-window course-deps-modal' onClick={stopProp}>
            <div className='course-deps-head'>
               <h3 className='modal-w-title'>Видалення курсу «{inventory ? inventory.courseName : ''}»</h3>
               <div className='course-deps-sub'>
                  Вибір рядка автоматично вибирає все, без чого він не може існувати. Якщо зняти таку залежність,
                  батьківський рядок отримає блокер і буде пропущений. Теми, які не вибрані, лишаються в бібліотеці - їм тільки знімається прив'язка до курсу.
                  Прогрес студентів у курсі видаляється остаточно, тому курс можна видалити лише разом з ним (рядки «Простір студента»).
               </div>
            </div>
            <div className='course-deps-toolbar'>
               <button type='button' className='course-deps-link' onClick={selectAll} disabled={!items.length}>Вибрати все</button>
               <button type='button' className='course-deps-link' onClick={clearAll} disabled={!selectedCount}>Скасувати вибір</button>
               <span className='course-deps-muted'>Вибрано {selectedCount} з {items.length}</span>
               <span className='course-deps-legend' title='Наведіть на іконку біля позиції, щоб побачити, що станеться'>
                  <span className='course-deps-role is-link'><IconRelation /></span> зв'язок
                  <span className='course-deps-role is-element'><IconFileText /></span> кінцевий елемент
               </span>
               {blockedCount ? <span className='course-deps-chip is-blocker'>Заблоковано: {blockedCount}</span> : ''}
            </div>
            <div className='course-deps-body'>
               {loading && !inventory ? (
                  <div className='course-deps-muted'>Завантаження...</div>
               ) : loadError ? (
                  <div className='course-deps-error'>{loadError}</div>
               ) : groups.map((group) => (
                  <div key={group.key} className='course-deps-group'>
                     <label className='course-deps-group-head'>
                        <input type='checkbox' checked={group.items.every((item) => selected.has(item.key))}
                           onChange={() => toggleGroup(group)} />
                        <span>{group.title}</span>
                        <span className='course-deps-muted'>{group.items.filter((item) => selected.has(item.key)).length} / {group.items.length}</span>
                     </label>
                     {group.items.map(renderItem)}
                  </div>
               ))}
               {inventory && inventory.copiesCount > 0 ? (
                  <div className='course-deps-note'>
                     Копії цього курсу у студентів ({inventory.copiesCount}) не видаляються: їм лише знімається посилання на цей курс.
                  </div>
               ) : ''}
            </div>
            {report ? renderReport() : ''}
            <div className='course-deps-foot'>
               <button type='button' className='form-button' onClick={close}>Закрити</button>
               <button type='button' className='form-button' disabled={busy || !selectedCount} onClick={() => run(true)}>
                  Перевірити без видалення
               </button>
               <button type='button' className='course-deps-danger' disabled={busy || !selectedCount} onClick={() => run(false)}>
                  {busy ? 'Видалення...' : 'Видалити вибране (' + selectedCount + ')'}
               </button>
            </div>
         </div>
      </div>
   );
}
